/**
 * WidgetGrid — empty placeholder grid per the dashboard surface
 * (Sketched in Claude Design 2026-05-10).
 *
 * Layouts:
 *   pro     → 6-col mixed grid, 3 rows:
 *               row 1: Check-ins (span 4) + Occupancy (span 2)   minH 220
 *               row 2: Reservations (span 3) + Tasks (span 3)
 *               row 3: Messages (span 4) + AddWidgetSlot (span 2)
 *   lite    → 2 columns, 4 slots (no Messages, no add).
 *   compact → single column, 4 slots in Lite / 5 in Pro.
 *
 * SCRUM-58 visual-fidelity round (2026-05-10):
 *  - Pro layout switched from flat 6-col single-span to the handoff's
 *    mixed-span layout. Check-ins now visually dominates row 1.
 *  - Each slot has an icon + handoff-canonical description.
 *  - `AddWidgetSlot` is now a real rendered tile in Pro mode (was
 *    missing — the only "Add widget" affordance previously lived in
 *    the greeting hero CTA).
 */
import * as React from 'react';
import { Icon, type IconName } from '@sparta/ui-kit';
import { WidgetSlot } from './WidgetSlot.js';

export interface WidgetGridProps {
  mode?: 'pro' | 'lite';
  compact?: boolean;
  onAddWidget?: () => void;
}

interface SlotDef {
  readonly id:
    | 'checkins'
    | 'occupancy'
    | 'reservations'
    | 'tasks'
    | 'messages';
  readonly title: string;
  readonly description: string;
  readonly icon: IconName;
}

const WIDGET_DEFS: Record<SlotDef['id'], SlotDef> = {
  checkins: {
    id: 'checkins',
    title: "Today's check-ins",
    description: 'Arrivals & departures.',
    icon: 'key',
  },
  occupancy: {
    id: 'occupancy',
    title: 'Occupancy',
    description: 'Live, plus 7-day trend.',
    icon: 'chart',
  },
  reservations: {
    id: 'reservations',
    title: 'Reservations',
    description: 'Latest bookings.',
    icon: 'receipt',
  },
  tasks: {
    id: 'tasks',
    title: 'Tasks',
    description: 'Open, assigned to me.',
    icon: 'checks',
  },
  messages: {
    id: 'messages',
    title: 'Messages',
    description: 'Guest threads.',
    icon: 'msg',
  },
};

function AddWidgetSlot({
  onClick,
}: {
  onClick?: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add a widget"
      // SCRUM-79 audit exemption: `min-h-[180px]` is the placeholder
      // slot's minimum height — handoff-specific value, no Tailwind
      // default matches (closest `min-h-44`=176 px or `min-h-48`=192 px).
      // Awaiting a branded `minHeight.widget-slot` token.
      // eslint-disable-next-line @sparta/no-pixel-literal-arbitrary
      className="bg-[var(--bg-sunken)] border-[1.5px] border-dashed border-[var(--border-default)] rounded-2xl flex flex-col items-center justify-center gap-2 p-6 text-[var(--fg-tertiary)] min-h-[180px] cursor-pointer w-full h-full text-center"
    >
      <span
        aria-hidden="true"
        className="w-10 h-10 rounded-full bg-[var(--bg-surface)] text-[var(--fg-secondary)] flex items-center justify-center"
      >
        <Icon name="plus" size={20} />
      </span>
      <span className="text-[13px] font-semibold text-[var(--fg-primary)]">
        Add a widget
      </span>
      {/* SCRUM-79 audit exemption: `max-w-[220px]` is the placeholder-
          slot helper-text width; no Tailwind default matches (closest
          `max-w-52`=208 px or `max-w-56`=224 px). Awaiting a branded
          `maxWidth.widget-helper` token. */}
      {/* eslint-disable-next-line @sparta/no-pixel-literal-arbitrary */}
      <span className="text-[11px] max-w-[220px]">
        Channel performance · Revenue · Availability heatmap…
      </span>
    </button>
  );
}

export function WidgetGrid({
  mode = 'pro',
  compact = false,
  onAddWidget,
}: WidgetGridProps): React.ReactElement {
  if (compact) {
    return (
      <section
        aria-label="Dashboard widgets"
        className="grid grid-cols-1 gap-3"
      >
        <WidgetSlot {...WIDGET_DEFS.checkins} minH={160} />
        <WidgetSlot {...WIDGET_DEFS.occupancy} minH={140} />
        <WidgetSlot {...WIDGET_DEFS.reservations} minH={160} />
        <WidgetSlot {...WIDGET_DEFS.tasks} minH={160} />
        {mode === 'pro' ? (
          <WidgetSlot {...WIDGET_DEFS.messages} minH={160} />
        ) : null}
      </section>
    );
  }

  if (mode === 'lite') {
    return (
      <section
        aria-label="Dashboard widgets"
        className="grid grid-cols-2 gap-4"
      >
        <WidgetSlot {...WIDGET_DEFS.checkins} />
        <WidgetSlot {...WIDGET_DEFS.occupancy} />
        <WidgetSlot {...WIDGET_DEFS.reservations} />
        <WidgetSlot {...WIDGET_DEFS.tasks} />
      </section>
    );
  }

  // Pro — 6-col mixed grid. `grid-cols-6` requires Tailwind's default
  // grid-template-columns scale; the preset retains 6 via its base
  // tailwindcss build.
  return (
    <section
      aria-label="Dashboard widgets"
      className="grid grid-cols-6 gap-4 auto-rows-[minmax(120px,auto)]"
    >
      <div className="col-span-4">
        <WidgetSlot {...WIDGET_DEFS.checkins} minH={220} />
      </div>
      <div className="col-span-2">
        <WidgetSlot {...WIDGET_DEFS.occupancy} minH={220} />
      </div>
      <div className="col-span-3">
        <WidgetSlot {...WIDGET_DEFS.reservations} />
      </div>
      <div className="col-span-3">
        <WidgetSlot {...WIDGET_DEFS.tasks} />
      </div>
      <div className="col-span-4">
        <WidgetSlot {...WIDGET_DEFS.messages} />
      </div>
      <div className="col-span-2">
        <AddWidgetSlot onClick={onAddWidget} />
      </div>
    </section>
  );
}
